import type { ApiContext } from "@hobbyshopify/backend-client";
import type * as AblyTypes from "ably";
import { ChangeNotifier, type Disposable } from "subtree.js/core";
import type { AuthContextProvider } from "trunk.js";
import type { BackendApi } from "../ops/api";

/**
 * A stream's realtime channels, as handles a controller owns.
 *
 * The client never publishes. Every payload originates on the server, after the
 * transaction that made it true — a client that could publish `break.reveal`
 * could fake a team assignment in front of a live audience, which is precisely
 * the accusation this product exists to avoid. The token the backend mints
 * carries subscribe and presence capabilities only, so this is enforced by Ably
 * rather than by this file behaving.
 *
 * Degrades rather than breaks. When realtime is not configured in an environment
 * the backend hands back a null token, `connected` stays false, and the caller
 * falls back to refetching — a cockpit that updates on a timer is worse than one
 * that updates instantly, and much better than one that does not load.
 *
 * `ably` is imported only once a token exists: the tests run these controllers
 * in Node with a client that has no realtime, and never load the SDK.
 */

export type LiveRealtimeEvent =
  | { type: "stream.status"; streamId: string; status: string; paused?: boolean }
  | {
      type: "break.updated";
      streamId: string | null;
      breakId: string;
      status: string;
      soldCount: number;
      slotCount: number;
    }
  | {
      type: "slot.updated";
      breakId: string;
      slots: { id: string; slotNumber: number; status: string; buyerName: string | null }[];
      soldCount: number;
      slotCount: number;
    }
  | {
      type: "break.reveal";
      breakId: string;
      streamId: string;
      randomizedAt: string;
      assignments: { slotNumber: number; label: string; status: string; buyerName: string | null }[];
    }
  | { type: "lineup.updated"; streamId: string; currentSegmentId: string | null }
  | { type: "spot.pinned"; streamId: string; breakId: string; slotId: string | null }
  | { type: "stream.host_changed"; streamId: string; hostName: string | null }
  | {
      type: "chat.message";
      id: string;
      streamId: string;
      userId: string;
      authorName: string;
      text: string;
      kind: "USER" | "PURCHASE" | "RIP_STARTED" | "DRAW" | "RIP_FINISHED";
      createdAt: string;
    }
  | { type: "chat.deleted"; streamId: string; id: string }
  | { type: "stage.queue"; streamId: string }
  | { type: "stage.invite"; streamId: string; userId: string };

export interface RealtimeCredentials {
  client: Pick<BackendApi, "live">;
  session: AuthContextProvider<ApiContext>;
  streamId: string;
}

/**
 * One realtime credential. Called again by Ably's `authCallback`: the token is
 * minted for an hour and a long stream would otherwise go quiet when the first
 * one expired. Null means realtime is not configured here, which is not an error.
 */
async function mintToken(o: RealtimeCredentials): Promise<unknown | null> {
  const r = await o.client.live.realtimeToken(await o.session.authorizeCall(), o.streamId);
  if (!r.ok) throw r.errors[0];
  return r.value.token;
}

/** A connected SDK client, or null when this environment has no realtime (or refused the token). */
async function openRealtime(o: RealtimeCredentials): Promise<AblyTypes.Realtime | null> {
  const first = await mintToken(o).catch(() => null);
  if (!first) return null;
  const Ably = await import("ably");
  return new Ably.Realtime({
    authCallback: (_params, callback) => {
      mintToken(o)
        .then((fresh) => callback(null, fresh as AblyTypes.TokenRequest))
        .catch((error) => callback(error as AblyTypes.ErrorInfo, null));
    },
  });
}

const ignore = () => {};

/** The stream and its chat: what changed, and how many are watching. */
export class StreamRealtime extends ChangeNotifier implements Disposable {
  #connected = false;
  #watching: number | null = null;
  #realtime: AblyTypes.Realtime | null = null;
  #disposed = false;

  constructor(
    o: RealtimeCredentials & {
      onEvent: (event: LiveRealtimeEvent) => void;
      /**
       * Join the audience, rather than only watching it.
       *
       * Off by default, and that default is load-bearing: presence is what the
       * room counts to say "N watching", so a host's cockpit entering it would
       * add the host to their own audience figure — on the one screen where that
       * number is a business fact rather than decoration.
       */
      enterPresence?: boolean;
    },
  ) {
    super();
    void this.#open(o);
  }

  get connected(): boolean {
    return this.#connected;
  }

  /** People in the room right now, or null before presence has answered. */
  get watching(): number | null {
    return this.#watching;
  }

  async #open(o: ConstructorParameters<typeof StreamRealtime>[0]): Promise<void> {
    const realtime = await openRealtime(o);
    if (!realtime) return;
    if (this.#disposed) {
      realtime.close();
      return;
    }
    this.#realtime = realtime;

    realtime.connection.on("connected", () => this.#setConnected(true));
    realtime.connection.on("disconnected", () => this.#setConnected(false));
    realtime.connection.on("failed", () => this.#setConnected(false));

    const receive = (message: AblyTypes.Message) => {
      if (!this.#disposed) o.onEvent(message.data as LiveRealtimeEvent);
    };

    // Three channels, matching the server: the stream, its chat, and one per
    // break. Chat is separate so a busy room cannot drown out a reveal.
    const stream = realtime.channels.get(`stream:${o.streamId}`);
    const chat = realtime.channels.get(`stream:${o.streamId}:chat`);
    // Closing the connection rejects an attach still in flight. That is the
    // page leaving, not a failure, so these swallow it.
    stream.subscribe(receive).catch(ignore);
    chat.subscribe(receive).catch(ignore);

    // Presence on the stream channel is what makes the viewer count real rather
    // than a guess from page loads.
    if (o.enterPresence) stream.presence.enter().catch(ignore);

    const count = () => {
      stream.presence
        .get()
        .then((members) => {
          if (this.#disposed) return;
          this.#watching = members.length;
          this.notifyListeners();
        })
        .catch(() => {
          // A presence read that fails is not worth a wrong number: keep the
          // last one rather than claiming the room emptied.
        });
    };

    stream.presence.subscribe(count).catch(ignore);
    count();
  }

  #setConnected(value: boolean): void {
    if (this.#disposed || this.#connected === value) return;
    this.#connected = value;
    this.notifyListeners();
  }

  override dispose(): void {
    this.#disposed = true;
    this.#realtime?.close();
    this.#realtime = null;
    this.#connected = false;
    this.#watching = null;
    super.dispose();
  }
}

export function connectStreamRealtime(
  o: RealtimeCredentials & { onEvent: (event: LiveRealtimeEvent) => void; enterPresence?: boolean },
): StreamRealtime {
  return new StreamRealtime(o);
}

/**
 * One break's slot grid.
 *
 * Its own channel and its own handle because the break a host is looking at
 * changes as the stream moves down its queue, and a 300-spot grid is the largest
 * and most frequent message during a sellout. Keeping it off the stream channel is
 * what stops a filling break drowning out the stream's own status.
 */
export function connectBreakRealtime(
  o: RealtimeCredentials & { breakId: string; onEvent: (event: LiveRealtimeEvent) => void },
): Disposable {
  let realtime: AblyTypes.Realtime | null = null;
  let disposed = false;

  void openRealtime(o).then((opened) => {
    if (!opened) return;
    if (disposed) {
      opened.close();
      return;
    }
    realtime = opened;
    realtime.channels
      .get(`break:${o.breakId}`)
      .subscribe((message) => {
        if (!disposed) o.onEvent(message.data as LiveRealtimeEvent);
      })
      .catch(ignore);
  });

  return {
    dispose() {
      disposed = true;
      realtime?.close();
      realtime = null;
    },
  };
}
